import { useCallback, useEffect } from "react";
import { UserSession, CreateUserSession, UpdateUserSession } from "../types/models/UserSession.d";
import { UpdateEntityUserSession } from "../types/models/UserSession";
import { useGetUserSessionListQuery, useInsertUserSessionMutation, useUpdateUserSessionMutation, useDeleteUserSessionMutation, } from "../apis/UserSession.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useUserSession(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertUserSessionMutation, { 
        isLoading: insertUserSessionLoading, 
        isSuccess: insertUserSessionIsSuccess,
        isError: insertUserSessionIsError,
        error: insertUserSessionError,
    }] = useInsertUserSessionMutation()
    const [updateUserSessionMutation, { 
        isLoading: updateUserSessionLoading,
        isSuccess: updateUserSessionIsSuccess,
        isError: updateUserSessionIsError,
        error: updateUserSessionError,
    }] = useUpdateUserSessionMutation()
    const [deleteUserSessionMutation, { 
        isLoading: deleteUserSessionLoading,
        isSuccess: deleteUserSessionIsSuccess,
        isError: deleteUserSessionIsError,
        error: deleteUserSessionError,
    }] = useDeleteUserSessionMutation()

    const loading =
        insertUserSessionLoading ||
        updateUserSessionLoading ||
        deleteUserSessionLoading ||
        false

    const insertUserSession = useCallback(async (userSession: CreateUserSession, redirectPath: string|undefined) => {
        try {
            const data = await insertUserSessionMutation(userSession).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertUserSessionMutation, navigate])
    
    useEffect(() => {
        if (insertUserSessionIsSuccess) {
            openSuccessAlert('User Session created')
        } else if (insertUserSessionIsError) {
            console.log("add error", insertUserSessionError)
            openFailAlert(t`User Session add error: ` + insertUserSessionError?.data?.message)
        }
    }, [
        insertUserSessionIsSuccess,
        insertUserSessionIsError,
        insertUserSessionError
    ]);

    const updateUserSession = useCallback(async (userSession: UpdateEntityUserSession, redirectPath: string|undefined) => {
        try {
            const data = await updateUserSessionMutation(userSession).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateUserSessionMutation, navigate])
    
    useEffect(() => {
        if (updateUserSessionIsSuccess) {
            openSuccessAlert(t`User Session updated`)
        } else if (updateUserSessionIsError) {
            console.log("update error", updateUserSessionError)
            openFailAlert(t`User Session update error: ` + updateUserSessionError?.data?.message)
        }
    }, [
        updateUserSessionIsSuccess,
        updateUserSessionIsError,
        updateUserSessionError
    ]);

    const deleteUserSession = useCallback((userSession: UserSession, redirectPath: string|undefined) => {
        openDialog(async () => {
            userSession = {
                ...userSession,
            }
            try {
                const { data } = await deleteUserSessionMutation(userSession);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteUserSessionMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteUserSessionIsSuccess) {
            openSuccessAlert(t`User Session deleted`)
        } else if (deleteUserSessionIsError) {
            console.log("delete error", deleteUserSessionError)
            openFailAlert(t`User Session delete error: ` + deleteUserSessionError?.data?.message)
        }
    }, [
        deleteUserSessionIsSuccess,
        deleteUserSessionIsError,
        deleteUserSessionError
    ]);

    return {
        loading,
        insertUserSession,
        updateUserSession,
        deleteUserSession
    }
}
